import { CheckIcon, ClockIcon, HomeIcon, SparklesIcon } from "@fawkes/ui";
import { useTranslate } from "@refinedev/core";

/**
 * De stand van een advertentie, als knoppen bij elke rij: nieuw in het asiel, gereserveerd, geplaatst
 * of buitenbeentje.
 *
 * <p><b>Eén stand tegelijk</b> (maintainer 2026-08-21). Technisch zijn het twee dingen — het lint op de
 * foto en de rij "De buitenbeentjes" op de home — maar voor wie een dier onder handen heeft is het één
 * vraag: wat is er met dit dier aan de hand. Een tweede aanzetten zet de vorige dus uit, en dezelfde nog
 * eens indrukken zet hem terug op automatisch: dan bepaalt het nachtelijke onderhoud het lint weer aan
 * de hand van hoe lang het dier er zit.
 *
 * <p>Ze zaten achter een `⋯`-menu, en dat is één klik en een muisreis te veel voor iets wat je in een
 * lijst van honderd advertenties tientallen keren per dag doet.
 *
 * <p>Het label noemt de STAND en niet de handeling: "Gereserveerd", niet "op gereserveerd zetten". Dat
 * is hoe een schakelknop hoort te lezen — `aria-pressed` vertelt of hij aan staat. Een eerdere versie
 * zette hier alleen tekens neer (☀ en ⏸): twee glyphs die niemand kon plaatsen en die als zwarte
 * vlekjes rendeerden.
 *
 * <p>Presentatie alleen: de aanroeper doet de mutatie en de verversing. Zo kan dezelfde rij knoppen in
 * de tabel, op de kaarten en op de advertentie zelf staan zonder dat er drie keer een endpoint wordt
 * bedacht.
 */
export type AdvertentieStand = "NIEUW" | "GERESERVEERD" | "GEPLAATST" | "BUITENBEENTJE";

export interface AdvertentieSnelActiesProps {
  /** Drie standen: `true` staat in de rij op de home, `false` bewust niet, `null` niet ingesteld. */
  buitenbeentje: boolean | null;
  /** De beschikbaarheid zoals hij in het dossier staat, bijv. "beschikbaar" of "Gereserveerd". */
  beschikbaarheid: string | null;
  /** De levensloopstatus; alleen `GEPLAATST` doet er hier toe. */
  status?: string | null;
  /** Een lopende mutatie: dan gaan ze op slot zodat je niet twee standen tegelijk verstuurt. */
  busy?: boolean;
  /** De stand; `null` betekent terug naar automatisch. */
  onStand: (stand: AdvertentieStand | null) => void;
  /** Kleiner, voor in een tabelrij of op een kaart. */
  compact?: boolean;
  className?: string;
}

/**
 * Welke stand er nu geldt.
 *
 * <p>Buitenbeentje gaat voor: dat is de enige die een medewerker uitdrukkelijk aanzet, en zolang hij
 * aanstaat is dát wat er met dit dier aan de hand is. De rest leest van de site af — het lint op
 * ikzoekbaas toont een lege waarde en "beschikbaar" allebei als "Nieuw in het asiel", dus die twee
 * zetten die knop aan.
 */
export function huidigeStand(
  beschikbaarheid: string | null,
  status: string | null | undefined,
  buitenbeentje?: boolean | null,
): AdvertentieStand | null {
  if (buitenbeentje === true) return "BUITENBEENTJE";
  const lint = (beschikbaarheid ?? "").trim().toLowerCase();
  if (status === "GEPLAATST" || lint === "geplaatst") return "GEPLAATST";
  if (lint === "gereserveerd") return "GERESERVEERD";
  if (lint === "" || lint === "beschikbaar") return "NIEUW";
  return null;
}

export function AdvertentieSnelActies({
  buitenbeentje,
  beschikbaarheid,
  status,
  busy,
  onStand,
  compact,
  className,
}: AdvertentieSnelActiesProps) {
  const t = useTranslate();
  const stand = huidigeStand(beschikbaarheid, status, buitenbeentje);

  const klasse = ["fawkes-adv-snel"];
  if (compact) klasse.push("fawkes-adv-snel--compact");
  if (className) klasse.push(className);

  // stopPropagation, want deze knoppen zitten in een rij en op een kaart die zelf al het dier openen —
  // zonder dit is elke klik op "gereserveerd" ook een klik op "openen".
  const klik = (e: React.MouseEvent, doe: () => void) => {
    e.stopPropagation();
    doe();
  };

  const knop = (welke: AdvertentieStand, icoon: React.ReactNode) => {
    const aan = stand === welke;
    return (
      <button
        type="button"
        className={"fawkes-adv-snel__knop" + (aan ? " fawkes-adv-snel__knop--aan" : "")}
        aria-pressed={aan}
        disabled={busy}
        title={t(aan
          ? "advertentieListView.actie.standAutomatisch"
          : `advertentieListView.actie.stand.${welke}`)}
        onClick={(e) => klik(e, () => onStand(aan ? null : welke))}
      >
        {icoon}
        <span>{t(`advertentieListView.stand.${welke}`)}</span>
      </button>
    );
  };

  return (
    <div className={klasse.join(" ")} role="group" aria-label={t("advertentieListView.acties")}>
      {knop("NIEUW", <CheckIcon width={14} height={14} aria-hidden="true" />)}
      {knop("GERESERVEERD", <ClockIcon width={14} height={14} aria-hidden="true" />)}
      {knop("GEPLAATST", <HomeIcon width={14} height={14} aria-hidden="true" />)}
      {knop("BUITENBEENTJE", <SparklesIcon width={14} height={14} aria-hidden="true" />)}
    </div>
  );
}
